import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { captureException, captureMessage, setUser } from '@sentry/capacitor';
import { describeHttpErrorResponse } from '../utils/sentry-http-error.util';

/** Sentry severity levels available for {@link SentryService.captureMessage}. */
type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

/**
 * Converts a value that isn't a genuine `Error` into one Sentry can properly
 * parse, title, and group.
 *
 * @remarks
 * **Why this exists (2026-08-05):** live Sentry events were showing up as
 * `"Object captured as exception with keys: error, headers, message, name,
 * ok, redirected, responseType, status, statusText, type, url"` — with no
 * message, no stack trace, and no useful grouping — whenever an Angular
 * `HttpErrorResponse` was passed straight to `captureException()` (confirmed
 * call sites: `globalErrorHandlerInterceptor` forwards every HTTP 5xx `err`
 * this way, and `AppSettingsService.fetchAndCache()`'s catch does too). See
 * {@link describeHttpErrorResponse}'s doc comment for the full root-cause
 * writeup — in short, `HttpErrorResponse` only structurally *implements*
 * `Error`, it doesn't *extend* it, so every one of Sentry's "is this an
 * Error?" checks fails for it.
 *
 * **This covers explicit `captureException()` calls only.** An
 * `HttpErrorResponse` that instead reaches Sentry via an *automatic* capture
 * path (an uncaught `window.onerror`, an unhandled promise rejection) is
 * covered by a separate `beforeSend` hook in `main.ts` — see that hook's own
 * doc comment for why this normalization alone can't reach it.
 *
 * A genuine `Error` instance passes through completely unchanged — this only
 * ever rewrites values Sentry would otherwise mis-render.
 *
 * @param error - The value passed to {@link SentryService.captureException}.
 * @returns A real `Error` Sentry can parse normally.
 */
function normalizeForSentry(error: unknown): unknown {
  if (error instanceof Error) {
    return error;
  }
  if (error instanceof HttpErrorResponse) {
    const { type, value } = describeHttpErrorResponse(error);
    const normalized = new Error(value, { cause: error });
    normalized.name = type;
    return normalized;
  }
  return error;
}

/**
 * Thin injectable wrapper around the Sentry Capacitor SDK.
 *
 * @remarks
 * Delegates to `@sentry/capacitor` which bridges native crash reporting
 * (iOS/Android) with the browser SDK. All calls are safe no-ops when Sentry
 * is not initialized — i.e. when `environment.sentryDsn` is empty.
 *
 * **Usage:**
 * - Call {@link captureException} from error handlers, catch blocks, or adapters.
 * - Call {@link setUser} after login and with `null` on logout so Sentry
 *   associates crashes with the correct process server.
 */
@Injectable({ providedIn: 'root' })
export class SentryService {
  /**
   * Captures an exception and forwards it to Sentry.
   *
   * @remarks
   * Non-`Error` values that Sentry can't parse natively (currently just
   * Angular's `HttpErrorResponse` — see {@link normalizeForSentry}) are
   * converted to a real `Error` first, so the event gets a proper message,
   * stack trace, and grouping instead of a bare "Object captured as
   * exception with keys: ..." fallback. The original value is still
   * reachable via the normalized error's `cause` and via `context`.
   *
   * @param error   - The error value to capture (any type is accepted).
   * @param context - Optional key/value pairs attached as `extra` data on the event.
   */
  captureException(error: unknown, context?: Record<string, unknown>): void {
    const normalized = normalizeForSentry(error);
    if (context) {
      captureException(normalized, { extra: context });
    } else {
      captureException(normalized);
    }
  }

  /**
   * Captures a custom message event.
   *
   * @param message - Human-readable text to send to Sentry.
   * @param level   - Severity level (defaults to `'info'`).
   */
  captureMessage(message: string, level: SeverityLevel = 'info'): void {
    captureMessage(message, level);
  }

  /**
   * Sets the authenticated user on the Sentry scope.
   *
   * Call after a successful login so Sentry can correlate events with the
   * process server. Pass `null` on logout to clear the scope.
   *
   * @param user - User identity, or `null` to reset.
   */
  setUser(user: { id: string; email?: string; username?: string } | null): void {
    setUser(user);
  }
}
